import React, { useRef, useState } from 'react';
import { Download, Upload, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createBackup, downloadBackup, uploadBackup } from '../db/backup';
import { db } from '../db';
import { supabase, supabaseSync } from '../lib/supabase';

export default function BackupManager() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleBackup = async () => {
    if (isBackingUp) return;
    setIsBackingUp(true);

    try {
      const backup = await createBackup();
      downloadBackup(backup);
      toast.success('백업 파일이 다운로드되었습니다');

      await db.logs.add({
        id: crypto.randomUUID(),
        timestamp: new Date(),
        type: 'backup',
        message: '데이터 백업이 완료되었습니다',
        metadata: {
          backupSize: JSON.stringify(backup).length,
        },
      });
    } catch (error: any) {
      console.error('백업 실패:', error.message);
      toast.error(`백업 생성에 실패했습니다: ${error.message}`);
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestore = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || isRestoring) return;

    try {
      const confirm = window.confirm(
        '백업을 복원하면 현재 데이터가 모두 삭제됩니다. 계속하시겠습니까?'
      );
      if (!confirm) return;

      setIsRestoring(true);
      const restoreToast = toast.loading('백업 복원 중...');

      // 동기화 비활성화
      supabaseSync.disableSync();

      await uploadBackup(file);

      // 동기화 복구
      supabaseSync.enableSync();

      await db.logs.add({
        id: crypto.randomUUID(),
        timestamp: new Date(),
        type: 'restore',
        message: '데이터 복원이 완료되었습니다',
        metadata: {
          fileName: file.name,
        },
      });

      toast.dismiss(restoreToast);
      toast.success('백업이 성공적으로 복원되었습니다');

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('복원 실패:', error.message);
      toast.error(`백업 복원에 실패했습니다: ${error.message}`);
    } finally {
      setIsRestoring(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleReset = async () => {
    if (isResetting) return;

    try {
      const confirm = window.confirm(
        '모든 데이터가 영구적으로 삭제됩니다. 정말 초기화하시겠습니까?\n' +
        '(이 작업은 되돌릴 수 없습니다)'
      );
      if (!confirm) return;

      setIsResetting(true);
      const resetToast = toast.loading('데이터 초기화 중...');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('로그인이 필요합니다');
      }

      // 동기화 비활성화
      supabaseSync.disableSync();

      if (navigator.onLine) {
        const { error: deleteError } = await supabase
          .from('skus')
          .delete()
          .or(`user_id.eq.${session.user.id},user_id.is.null`);

        if (deleteError) {
          throw new Error(`서버 데이터 삭제 중 오류: ${deleteError.message}`);
        }

        const { data: remainingSkus, error: checkError } = await supabase
          .from('skus')
          .select('id')
          .or(`user_id.eq.${session.user.id},user_id.is.null`);

        if (checkError) {
          throw new Error(`서버 상태 확인 중 오류: ${checkError.message}`);
        }

        if (remainingSkus && remainingSkus.length > 0) {
          throw new Error('서버 데이터가 완전히 삭제되지 않았습니다');
        }
      } else {
        toast.warn('오프라인 상태: 로컬 데이터만 초기화됩니다.');
      }

      await db.transaction('rw', [db.skus, db.logs], async () => {
        await db.skus.clear();
        await db.logs.clear();

        await db.logs.add({
          id: crypto.randomUUID(),
          timestamp: new Date(),
          type: 'settings',
          message: '데이터가 초기화되었습니다',
          metadata: {
            action: 'reset',
            userId: session.user.id,
          },
        });
      });

      // 동기화 복구
      supabaseSync.enableSync();

      toast.dismiss(resetToast);
      toast.success('모든 데이터가 초기화되었습니다');

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('초기화 실패:', error.message);
      toast.error(`데이터 초기화에 실패했습니다: ${error.message}`);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold mb-4">데이터 백업 관리</h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                정기적인 백업을 권장합니다
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                데이터 손실을 방지하기 위해 주기적으로 백업을 진행해주세요.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleBackup}
            disabled={isBackingUp}
            className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm md:text-base ${
              isBackingUp
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
          >
            <Download className="w-4 h-4" />
            <span className="whitespace-nowrap">
              {isBackingUp ? '백업 중...' : '백업 다운로드'}
            </span>
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isRestoring}
            className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm md:text-base ${
              isRestoring
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span className="whitespace-nowrap">
              {isRestoring ? '복원 중...' : '백업 복원'}
            </span>
          </button>
        </div>

        <div className="border-t pt-4 mt-4">
          <h3 className="text-base font-semibold mb-2">데이터 초기화</h3>
          <p className="text-sm text-gray-600 mb-3">
            모든 SKU 데이터와 작업 이력을 삭제합니다. 이 작업은 되돌릴 수 없습니다.
          </p>
          <button
            onClick={handleReset}
            disabled={isResetting}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm md:text-base ${
              isResetting
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-red-50 text-red-700 hover:bg-red-100'
            }`}
          >
            <Trash2 className="w-4 h-4" />
            <span className="whitespace-nowrap">
              {isResetting ? '초기화 중...' : '데이터 초기화'}
            </span>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleRestore}
          className="hidden"
        />
      </div>
    </div>
  );
}